import { useFieldArray, useFormContext, Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Plus, Trash2 } from "lucide-react";
import {
  createBlankQuestion,
  type SurveyFormData,
} from "../../schemas/surveySchema";

/**
 * Multi-question editor rendered inside SurveyCreatePage's "Questions" tab.
 *
 * Modeled on SessionTypeManager: `useFieldArray` over `questions`, each
 * row is a Paper card with prompt + type + options/presentation. Removal
 * is disabled when only one question remains (surveys must have ≥1
 * question per the shared schema). No drag-reorder — matches the
 * SessionTypeManager precedent.
 */
export function QuestionsManager() {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<SurveyFormData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "questions",
  });

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Button
          onClick={() => append(createBlankQuestion())}
          startIcon={<Plus size={20} />}
          variant="outlined"
        >
          Add Question
        </Button>
        <Typography variant="body2" color="text.secondary">
          {fields.length} question{fields.length === 1 ? "" : "s"}
        </Typography>
      </Box>

      {errors.questions?.root && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {errors.questions.root.message}
        </Typography>
      )}

      {fields.map((field, index) => {
        const questionType = watch(`questions.${index}.type`);

        return (
          <Paper key={field.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Question {index + 1}
              </Typography>
              <IconButton
                onClick={() => remove(index)}
                disabled={fields.length === 1}
                aria-label={`Remove question ${index + 1}`}
                color="error"
              >
                <Trash2 size={20} />
              </IconButton>
            </Box>

            <Controller
              name={`questions.${index}.prompt`}
              control={control}
              render={({ field: f, fieldState }) => (
                <TextField
                  {...f}
                  label="Question"
                  placeholder="e.g., How would you rate this session?"
                  fullWidth
                  multiline
                  minRows={2}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{ mb: 2 }}
                />
              )}
            />

            <Controller
              name={`questions.${index}.type`}
              control={control}
              render={({ field: f }) => (
                <TextField
                  {...f}
                  label="Type"
                  select
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="single_choice">Multiple Choice</MenuItem>
                  <MenuItem value="free_text">Free Text</MenuItem>
                  <MenuItem value="rating">Rating (1-5)</MenuItem>
                </TextField>
              )}
            />

            {questionType === "single_choice" && (
              <Controller
                name={`questions.${index}.options`}
                control={control}
                render={({ field: f, fieldState }) => (
                  <TextField
                    {...f}
                    label="Options (one per line)"
                    placeholder={"Option A\nOption B\nOption C"}
                    fullWidth
                    multiline
                    minRows={3}
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ??
                      "Enter at least 2 options, one per line"
                    }
                    sx={{ mb: 2 }}
                  />
                )}
              />
            )}

            {questionType === "rating" && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Attendees will see a 1-5 star rating widget.
              </Typography>
            )}

            {questionType === "free_text" && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Attendees will see a free-response text box.
              </Typography>
            )}

            {/* Presentation metadata block (Power BI deferred) */}
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Dashboard Presentation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              These labels customize the Power BI live dashboard. The
              dashboard surface is deferred for v1 — fields are kept here
              so authoring doesn&apos;t need to be re-built later, but
              values entered now have no effect yet.
            </Typography>

            <Controller
              name={`questions.${index}.responseLabel`}
              control={control}
              render={({ field: f }) => (
                <TextField
                  {...f}
                  label="Response count label"
                  placeholder="e.g., portfolio managers"
                  fullWidth
                  disabled
                  helperText='Noun shown next to the count. Default: "responses"'
                  sx={{ mb: 2 }}
                />
              )}
            />
            <Controller
              name={`questions.${index}.chartSubtitle`}
              control={control}
              render={({ field: f }) => (
                <TextField
                  {...f}
                  label="Chart subtitle"
                  placeholder="e.g., 1 = low conviction, 5 = high"
                  fullWidth
                  disabled
                  helperText="Shown below the chart title"
                  sx={{ mb: 2 }}
                />
              )}
            />

            {questionType === "rating" && (
              <>
                <Controller
                  name={`questions.${index}.ratingAxisLabel`}
                  control={control}
                  render={({ field: f }) => (
                    <TextField
                      {...f}
                      label="Rating axis label"
                      placeholder="e.g., Conviction Level"
                      fullWidth
                      disabled
                      helperText='Histogram X-axis label. Default: "Rating"'
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name={`questions.${index}.ratingScaleMin`}
                  control={control}
                  render={({ field: f }) => (
                    <TextField
                      {...f}
                      label="Scale low-end label"
                      placeholder="e.g., Low conviction"
                      fullWidth
                      disabled
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name={`questions.${index}.ratingScaleMax`}
                  control={control}
                  render={({ field: f }) => (
                    <TextField
                      {...f}
                      label="Scale high-end label"
                      placeholder="e.g., High conviction"
                      fullWidth
                      disabled
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              </>
            )}

            {questionType === "single_choice" && (
              <Controller
                name={`questions.${index}.optionAxisLabel`}
                control={control}
                render={({ field: f }) => (
                  <TextField
                    {...f}
                    label="Answer axis label"
                    placeholder="e.g., Portfolio Concern"
                    fullWidth
                    disabled
                    helperText='Bar chart Y-axis label. Default: "Answer"'
                    sx={{ mb: 2 }}
                  />
                )}
              />
            )}
          </Paper>
        );
      })}
    </Box>
  );
}
